// src/components/desk/ContractPanel.jsx — the meeting card's contracts BLOCK, and the door to
// the contract page.
//
// Owner brief (core/CONTRACTS_DESIGN_2026-09-10.md): "we need to start working on contract
// creation from system and sending it to parties by a link on whatsapp or email like signify
// does … we have all the details in meeting for contract forming." Then, the same day: "we
// need this to be a separate link. we press create contract then choose a type and the opens
// contract page with inherited values" · "it must be linkable to send".
//
// So this panel (the Desk half of B1249–B1252, INSIDE `CoreMeetingDetail` under the binder
// readout) no longer composes, previews or sends anything — that all lives on the page
// (`#contract/<id>`, ContractPage.jsx; shared pieces in ContractComposer.jsx). Since the design
// build (2026-09-11, designer 2a "meeting card → contracts block") it is a DARK card:
//   1. one row per contract on this meeting — title · status chip · "vN · created · langs" —
//      each an `<a href>` to the page: a real link, so it can be opened in a new tab or
//      copied, which is what "linkable" means;
//   2. a dashed "+ New contract" row → the TYPE chooser (round 1 decision 10: tiny — one
//      live row per template the server offers, with its reviewed languages, and greyed
//      "soon" rows for the kinds that do not exist yet: agency service agreement, sale).
// A server without the contract module (404 on the list) draws nothing — a bundle deployed
// ahead of the server restart must not paint an error on every meeting card.

import { useEffect, useRef, useState } from 'react'
import { t, tOr } from '../../lib/i18n.js'
import { Icon } from './../Icon.jsx'
import { listContracts, listContractTemplates, deleteContractFormDraft } from '../../db/core.js'
import { DEFAULT_TEMPLATE, L, fmtWhen, contractErrorText } from './ContractComposer.jsx'
import { StatusChip } from './contract/StatusChip.jsx'
import { BAR, contractFullTitle, contractShortName } from './contract/paper.js'

export function contractPageHash(id) { return `#contract/${id}` }
export function newContractHash(meetingId, templateCode, feeModel = '') {
  return `#contract/new?meeting=${encodeURIComponent(meetingId)}&template=${encodeURIComponent(templateCode || DEFAULT_TEMPLATE)}${feeModel ? `&fee_model=${encodeURIComponent(feeModel)}` : ''}`
}
// 🏷️ THE SERVICE AGREEMENT IS TWO TYPES, picked per case (owner 2026-10-10): only the owner pays, or owner and tenant
// each pay their part (the tenant then signs the Tenant Agency Agreement). It used to follow the property's city.
export const FEE_MODELS = ['owner', 'both']

// The kinds the chooser promises and greys until a template of that kind exists.
// The agency service agreement and the binder went live with migration 1055 (2026-09-11);
// only a sale contract is still to come.
const SOON_KINDS = [
  { kind: 'sale', key: 'desk_contract_type_sale_soon' },
]

// 🔑 A CLIENT IS NOT WHAT EVERY DOCUMENT NEEDS (owner, 2026-09-12: "there is no need for client
// to exist to create თანამშრომლობის ხელშეკრულება — there are needed client for two other
// instances, not here"). The service agreement is between the AGENCY and the OWNER of the flat;
// the tenant does not appear in it and may not exist yet. The rental and the ბე do need one —
// a tenant and a giver respectively.
// 🔑 THE SERVER ANSWERS THIS NOW: every template row carries `needs` (["property"] or
// ["property","client"]), derived from its own `PARTY_ROLES` — so a FOURTH document type,
// added there, greys and un-greys correctly here with no change to this file. The owner said
// exactly that was coming ("I'll create now a 4th contract that won't need client"), and a
// hardcoded table would have been wrong the day it arrived.
// The map below is the FALLBACK for a server that predates `needs`, and nothing else. Do not
// extend it for a new kind — add the kind to the server's PARTY_ROLES.
const KIND_NEEDS = {
  rent_residential: ['property', 'client'],
  rent_commercial: ['property', 'client'],
  binder: ['property', 'client'],
  agency_service: ['property'],
  tenant_agency_service: ['property', 'client'],
  sale_preliminary: ['property', 'client'],
}
const kindOf = tp => String(tp?.kind || tp?.code || '').replace(/_v\d+$/, '')
// What this meeting cannot supply for that document. Property is required by all of them —
// every one of these papers is about a specific flat.
function missingFor(tp, has) {
  const needs = Array.isArray(tp?.needs) && tp.needs.length ? tp.needs : (KIND_NEEDS[kindOf(tp)] || ['property'])
  return needs.filter(n => !has[n])
}

// `canCompose`: a NEW contract needs both sides (a tenant and a flat); the LIST of contracts
// this meeting already has needs only the meeting. Owner 2026-09-11: a signed contract was
// invisible from the meeting on the web because the whole panel was gated on the two ids —
// an existing document must always be reachable from its meeting.
// 🚨 A BLOCK THAT DISAPPEARS IS A BUG REPORT WAITING TO HAPPEN (owner, 2026-09-12: "where has
// create contract button gone" · "some things just get hidden"). Every gate below used to
// return null, so the same screen showed the door, then showed nothing, and the difference was
// invisible: the panel had been rendering only because a contract already existed on that
// meeting, and deleting the test contracts took the whole block with it.
// So the gates SPEAK now. The panel renders whenever the modal has a meeting, and when it
// cannot offer a new contract it says which half is missing — a client, a flat, or a core
// record. Same rule the repo already writes down for locks (diagnosis rule 6): a control that
// is not there must be replaced by the reason it is not there, not by empty space.
// ⚠️ ONE silence is kept on purpose: a server with no contract module (404) still draws
// nothing, because that is a deploy skew that will resolve itself in minutes and is not a fact
// about this meeting.
export function ContractPanel({ meeting, canAct = true, canCompose = true }) {
  const meetingId = meeting?.core_meeting_id
  const [list, setList] = useState(null)         // null = loading
  const [err, setErr] = useState('')
  const [unavailable, setUnavailable] = useState(false) // server without the contract module
  const [choosing, setChoosing] = useState(false)
  const [templates, setTemplates] = useState(null) // null = not fetched yet
  const [drafts, setDrafts] = useState([])         // unfinished forms on this meeting (core 1936)
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])

  useEffect(() => {
    setList(null); setErr(''); setChoosing(false)
    if (!meetingId) return
    let cancelled = false
    listContracts(meetingId).then(r => {
      if (cancelled || !alive.current) return
      setList(r.contracts || []); setDrafts(r.form_drafts || [])
    }).catch(e => {
      if (cancelled || !alive.current) return
      if (e.status === 404) { setUnavailable(true); return }
      setErr(contractErrorText(e)); setList([])
    })
    return () => { cancelled = true }
  }, [meetingId])

  // The type chooser. An older server without `contract-templates` still has the one
  // template it always had — offer that rather than an empty list behind a live button.
  async function openChooser() {
    setChoosing(true)
    if (templates) return
    try {
      const r = await listContractTemplates()
      if (!alive.current) return
      setTemplates(r.templates || [])
    } catch (e) {
      if (!alive.current) return
      if (e.status === 404) setTemplates([{ code: DEFAULT_TEMPLATE, kind: 'rent_residential', title_ka: t('desk_contract_new_rent'), title_en: t('desk_contract_new_rent'), langs_reviewed: ['ka'] }])
      else { setTemplates([]); setErr(contractErrorText(e)) }
    }
  }

  if (unavailable) return null
  // WHY a new contract cannot be drawn here, in the panel's own words. Ordered by what the
  // agent can do about it: a legacy meeting cannot hold one at all, a missing side can be
  // filled in above.
  // ⚠️ The BLOCK is blocked only when NO document is possible. A missing client does not
  // belong here any more — it belongs on the two rows that need one, inside the chooser.
  const has = { property: !!meeting?.property_id, client: !!meeting?.client_party_id }
  const blocked = !meetingId ? t('desk_contract_needs_core')
    : !has.property ? t('desk_contract_needs_property')
    : !canAct ? t('desk_contract_needs_rights')
    : !canCompose ? t('desk_contract_needs_property')
    : ''
  const soon = SOON_KINDS.filter(s => !(templates || []).some(tp => String(tp.kind || tp.code || '').startsWith(s.kind)))

  return (
    <div className="pt-2 mt-2" data-testid="contract-panel">
      <div className={`${BAR} rounded-xl p-3 space-y-2`}>
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-[10px] tracking-[.14em] uppercase text-[#F7F5F0]/50 flex items-center gap-1.5">
            <Icon name="file-text" size={11} /> {t('desk_contract_title')}
          </div>
          {list && list.length > 0 && <span className="font-mono text-[10px] text-[#F7F5F0]/40">{list.length}</span>}
        </div>

        {meetingId && list === null && !err && <div className="text-[11px] text-[#F7F5F0]/50">…</div>}
        {(!meetingId || (list && list.length === 0)) && !choosing && (
          <div className="text-[12px] text-[#F7F5F0]/55 italic">{t('desk_contract_none')}</div>
        )}
        {/* …and, when there is no door, the reason there is none. */}
        {blocked && <div className="text-[11.5px] leading-snug text-[#F7F5F0]/45" data-testid="contract-blocked">{blocked}</div>}

        {list && list.length > 0 && (
          <div className="divide-y divide-[#F7F5F0]/10">
            {list.map(c => (
              <a key={c.id} href={contractPageHash(c.id)} title={t('desk_contract_open_page')}
                className="w-full flex items-center gap-2 text-left text-[13px] py-2 hover:bg-[#F7F5F0]/5 rounded px-1 -mx-1 min-w-0" data-testid="contract-row">
                <span className="min-w-0 flex-1">
                  <span className="block truncate" title={contractFullTitle(c)}>{contractShortName(c)}</span>
                  <span className="block font-mono text-[10px] text-[#F7F5F0]/50 truncate">
                    v{c.current_version?.version ?? '—'} · {fmtWhen(c.created_at)} · {(c.langs || []).join(' ')}
                  </span>
                </span>
                <StatusChip contract={c} />
                <Icon name="external-link" size={11} />
              </a>
            ))}
          </div>
        )}

        {/* 📝 UNFINISHED FORMS (owner 2026-09-22): a form somebody started is a draft the moment they
            typed, listed here with a trash icon; opening it restores the answers. */}
        {drafts.length > 0 && (
          <div className="divide-y divide-[#F7F5F0]/10" data-testid="contract-drafts">
            {drafts.map(d => (
              <div key={d.id} className="flex items-center gap-2 text-[13px] py-2 px-1 -mx-1 min-w-0" data-testid="contract-draft-row">
                <a href={newContractHash(meetingId, d.template_code)} className="min-w-0 flex-1 hover:underline" title={t('desk_contract_draft_open')}>
                  <span className="block truncate">{tOr(`desk_contract_tpl_${d.template_code}`, d.template_code)}</span>
                  <span className="block font-mono text-[10px] text-[#F7F5F0]/50 truncate">
                    {t('desk_contract_draft_unfinished')} · {fmtWhen(d.updated_at)}{d.created_by_name && !d.mine ? ` · ${d.created_by_name}` : ''}
                  </span>
                </a>
                <button type="button" title={t('desk_contract_draft_delete')} aria-label={t('desk_contract_draft_delete')} data-testid="contract-draft-delete"
                  className="p-1 rounded text-[#F7F5F0]/60 hover:text-red-300 hover:bg-[#F7F5F0]/10"
                  onClick={async () => {
                    if (!window.confirm(t('desk_contract_draft_delete_confirm'))) return
                    try { await deleteContractFormDraft(d.id); if (alive.current) setDrafts(x => x.filter(y => y.id !== d.id)) }
                    catch (e) { if (alive.current) setErr(contractErrorText(e)) }
                  }}>
                  <Icon name="trash" size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {!blocked && !choosing && (
          <button type="button" onClick={openChooser} data-testid="contract-new-row"
            className="w-full flex items-center gap-1.5 rounded-lg border border-dashed border-[#F7F5F0]/30 px-2 py-2 text-[13px] text-[#F7F5F0]/80 hover:border-[#F7F5F0]/60">
            <Icon name="plus" size={12} /> {t('desk_contract_new_contract')}
          </button>
        )}

        {/* The type chooser: press "create contract", then choose a type (owner 2026-09-10).
            One live row per template the server offers; the rest "soon". */}
        {choosing && (
          <div className="rounded-lg border border-[#F7F5F0]/15 px-2 py-1.5 space-y-0.5 text-[13px]" data-testid="contract-type-chooser">
            <div className="flex items-center justify-between gap-2 pb-1">
              <span className="font-mono text-[10px] tracking-[.14em] uppercase text-[#F7F5F0]/50">{t('desk_contract_choose_type')}</span>
              <button type="button" className="text-[11px] text-[#F7F5F0]/60" onClick={() => setChoosing(false)}>{t('desk_contract_cancel')}</button>
            </div>
            {templates === null && <div className="text-[11px] text-[#F7F5F0]/50">…</div>}
            {templates && templates.length === 0 && <div className="text-[11px] text-[#F7F5F0]/50 italic">{t('desk_contract_types_none')}</div>}
            {templates && templates.map(tp => {
              // A type this meeting cannot fill is shown and EXPLAINED rather than hidden: the
              // agent needs to know the paper exists and what it is waiting for.
              const miss = missingFor(tp, has)
              if (miss.length) return (
                <div key={tp.code} data-testid="contract-type-blocked" data-missing={miss.join(',')}
                  className="flex items-center gap-2 py-1.5 px-1 -mx-1 rounded opacity-45 cursor-default" aria-disabled="true">
                  <Icon name="file-text" size={11} />
                  <span className="min-w-0 flex-1 truncate" title={contractFullTitle(tp)}>{contractShortName(tp)}</span>
                  <span className="text-[10px] text-[#F7F5F0]/70 shrink-0">
                    {miss.includes('client') ? t('desk_contract_type_needs_client') : t('desk_contract_type_needs_property')}
                  </span>
                </div>
              )
              return (tp.kind === 'agency_service' ? FEE_MODELS : ['']).map(fm => (
                <a key={tp.code + fm} href={newContractHash(meetingId, tp.code, fm)} data-testid="contract-type-row" data-fee-model={fm || undefined}
                  className="flex items-center gap-2 py-1.5 px-1 -mx-1 rounded hover:bg-[#F7F5F0]/5">
                  <Icon name="file-text" size={11} />
                  <span className="min-w-0 flex-1 truncate" title={contractFullTitle(tp)}>{contractShortName(tp)}{fm ? ` — ${t(`desk_contract_fee_model_${fm}`)}` : ''}</span>
                  {tp.legal_review === 'pending'
                    // ⚖️ drafted and previewed only — the server refuses links and signatures for it
                    ? <span className="text-[10px] text-amber-300 shrink-0" data-testid="contract-type-legal-pending">{t('desk_contract_legal_pending_short')}</span>
                    : <span className="font-mono text-[10px] text-[#F7F5F0]/50 shrink-0">{(tp.langs_reviewed || []).join(' ')}</span>}
                  <Icon name="external-link" size={11} />
                </a>
              ))
            })}
            {templates && soon.map(s => (
              <div key={s.kind} className="flex items-center gap-2 py-1.5 px-1 -mx-1 opacity-40 cursor-default" aria-disabled="true" data-testid="contract-type-soon">
                <Icon name="file-text" size={11} />
                <span className="min-w-0 flex-1 truncate">{t(s.key)}</span>
                <span className="font-mono text-[9px] tracking-[.12em] uppercase rounded border border-[#F7F5F0]/30 px-1">{t('desk_contract_soon')}</span>
              </div>
            ))}
          </div>
        )}

        {err && <p className="text-[11px] text-red-300">{err}</p>}
      </div>
    </div>
  )
}
